import { useEffect, useRef } from 'react';
import { useIsClient } from '@fiverr-private/hooks';
import { usePageVisibility } from '../../../../shared/hooks/usePageVisibility';
import { usePrefersReducedMotion } from '../../../../shared/hooks/usePrefersReducedMotion';
import { runTypewriter, TypewriterOptions, TypewriterProgress, TypewriterTick } from './runTypewriter';

const INITIAL_PROGRESS: TypewriterProgress = { phraseIndex: 0, charCount: 0, isDeleting: false };

export const useTypewriterEngine = (
    phrases: string[],
    active: boolean,
    onTick: TypewriterTick,
    options?: TypewriterOptions
) => {
    const isClient = useIsClient();
    const isPageVisible = usePageVisibility();
    const prefersReducedMotion = usePrefersReducedMotion();
    const onTickRef = useRef(onTick);
    const progressRef = useRef(INITIAL_PROGRESS);
    const phrasesRef = useRef(phrases);
    const { typingSpeedMs, deletingSpeedMs, pauseAfterTypedMs, pauseBeforeNextMs } = options ?? {};

    useEffect(() => {
        onTickRef.current = onTick;
    });

    if (phrasesRef.current !== phrases) {
        phrasesRef.current = phrases;
        progressRef.current = INITIAL_PROGRESS;
    }

    const isRunning = Boolean(active && isClient && isPageVisible && !prefersReducedMotion && phrases.length);

    useEffect(() => {
        if (!isRunning) {
            return undefined;
        }

        return runTypewriter(
            phrases,
            (text, phraseIndex, progress) => {
                progressRef.current = progress;
                onTickRef.current(text, phraseIndex, progress);
            },
            {
                typingSpeedMs,
                deletingSpeedMs,
                pauseAfterTypedMs,
                pauseBeforeNextMs,
                initialProgress: progressRef.current,
            }
        );
    }, [isRunning, phrases, typingSpeedMs, deletingSpeedMs, pauseAfterTypedMs, pauseBeforeNextMs]);

    return isRunning;
};
